import { useEffect, useRef, useState, type ReactElement } from 'react';
import { CheckCircle2, Clock, AlertCircle, TrendingUp, Wallet, PiggyBank, Building2, MoreVertical, CalendarClock, Download } from 'lucide-react';
import {
  elsterApi, taxApi,
  type TaxOverview as TaxOverviewData, type TaxFristUstva,
  type TaxNotice, type ElsterCertificate,
} from '../../lib/api';
import { Button, Card, ErrorNote, Field, euro } from '../ui';

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

/** Dringlichkeits-Rang: 0=ueberfaellig, 1=offen, 2=erledigt. Kleiner = oben. */
function urgencyRank(state: string, dueDate: string | undefined, todayIso: string): number {
  if (state === 'eingereicht') return 2;
  if (state === 'ueberfaellig') return 0;
  if (dueDate && dueDate < todayIso) return 0; // offen + Frist ueberschritten
  return 1;
}

/**
 * Bescheide aus dem ELSTER-Postfach: abholen, auflisten, als PDF speichern.
 *
 * Der Abruf braucht Zertifikat + PIN wie eine Übermittlung — es ist der
 * Eigenabruf mit dem Zertifikat des Nutzers. Abgeholte Bescheide werden
 * gespeichert, weil ELSTER quittierte Bereitstellungen aus dem Postfach nimmt.
 */
function Bescheide({ companyId }: { companyId: string }): ReactElement {
  const [notices, setNotices] = useState<TaxNotice[]>([]);
  const [certs, setCerts] = useState<ElsterCertificate[]>([]);
  const [dialog, setDialog] = useState(false);
  const [certId, setCertId] = useState('');
  const [pin, setPin] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [info, setInfo] = useState<string | null>(null);

  function load() {
    taxApi.notices(companyId).then(setNotices).catch(() => setNotices([]));
  }
  useEffect(() => {
    load();
    elsterApi.listCertificates(companyId)
      .then((r) => { setCerts(r.certificates); if (r.certificates[0]) setCertId(r.certificates[0].id); })
      .catch(() => setCerts([]));
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [companyId]);

  async function abholen() {
    setBusy(true); setError(null); setInfo(null);
    try {
      const r = await taxApi.fetchNotices(companyId, certId, pin);
      setInfo(
        r.neu > 0
          ? `${r.neu} neue${r.neu === 1 ? 'r Bescheid' : ' Bescheide'} abgeholt.`
          : r.gesamt > 0
            ? 'Keine neuen Bescheide — alle bereits abgeholt.'
            : 'Im Postfach liegt derzeit nichts bereit.',
      );
      setDialog(false); setPin('');
      load();
    } catch (e) {
      setError(e);
    } finally { setBusy(false); }
  }

  async function pdfLaden(n: TaxNotice) {
    try {
      const blob = await taxApi.noticePdf(companyId, n.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${n.label}${n.year ? `-${n.year}` : ''}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { setError(e); }
  }

  return (
    <div>
      <div className="mb-3 flex items-center justify-between gap-3">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Bescheide vom Finanzamt</h3>
        <Button variant="secondary" onClick={() => setDialog(true)} disabled={certs.length === 0}>
          <Download className="mr-1.5 inline h-4 w-4" />Bescheide abholen
        </Button>
      </div>

      {certs.length === 0 && (
        <p className="mb-3 text-xs text-gray-500 dark:text-gray-400">
          Für den Abruf wird ein ELSTER-Zertifikat benötigt — dasselbe wie für die Übermittlung.{' '}
          <a href="/app/einstellungen/elster" className="underline">Jetzt hinterlegen</a>
        </p>
      )}
      {info && <p className="mb-3 rounded-lg bg-green-50 px-3 py-2 text-sm text-green-800 dark:bg-green-950/30 dark:text-green-300">{info}</p>}
      {error ? <div className="mb-3"><ErrorNote error={error} /></div> : null}

      <Card className="divide-y divide-gray-100 dark:divide-gray-700">
        {notices.length === 0 ? (
          <p className="py-3 text-sm text-gray-500 dark:text-gray-400">
            Noch keine Bescheide abgeholt.
          </p>
        ) : notices.map((n) => (
          <div key={n.id} className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0">
            <div className="min-w-0">
              <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {n.label}{n.year ? ` ${n.year}` : ''}
              </div>
              <div className="flex flex-wrap items-center gap-2 text-xs text-gray-400">
                {n.taxOffice && <span>Finanzamt {n.taxOffice}</span>}
                {n.issuedAt && <span>vom {fmtDate(n.issuedAt)}</span>}
              </div>
            </div>
            {n.hasPdf ? (
              <button onClick={() => pdfLaden(n)}
                className="shrink-0 text-sm text-blue-600 hover:underline dark:text-blue-400">
                PDF
              </button>
            ) : (
              <span className="shrink-0 text-xs text-gray-400">kein PDF</span>
            )}
          </div>
        ))}
      </Card>

      {dialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setDialog(false)}>
          <div className="w-full max-w-sm rounded-2xl bg-white p-5 shadow-xl dark:bg-gray-900" onClick={(e) => e.stopPropagation()}>
            <h3 className="mb-1 text-lg font-semibold text-gray-900 dark:text-gray-100">Bescheide abholen</h3>
            <p className="mb-4 text-xs text-gray-500 dark:text-gray-400">
              Holt alle bereitliegenden Bescheide und Mitteilungen aus deinem ELSTER-Postfach.
            </p>
            <div className="space-y-3">
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Zertifikat</span>
                <select value={certId} onChange={(e) => setCertId(e.target.value)}
                  className="w-full rounded-xl border border-gray-300 px-4 py-2.5 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100">
                  {certs.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </label>
              <Field label="PIN" type="password" value={pin} onChange={setPin} placeholder="Zertifikats-PIN" />
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setDialog(false)}>Abbrechen</Button>
              <Button onClick={abholen} busy={busy} disabled={!certId || !pin}>Abholen</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** ⋮-Menue: Status manuell setzen (extern eingereicht / wieder offen). */
function StatusMenu({ companyId, type, period, isDone, onDone }: {
  companyId: string; type: string; period: string; isDone: boolean; onDone: () => void;
}): ReactElement {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function onClick(e: MouseEvent) { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); }
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);
  async function set(status: 'extern_eingereicht' | 'offen') {
    setBusy(true);
    try { await elsterApi.setManualStatus(companyId, type, period, status); onDone(); } finally { setBusy(false); setOpen(false); }
  }
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((o) => !o)} disabled={busy}
        className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
        aria-label="Status ändern">
        <MoreVertical className="h-4 w-4" />
      </button>
      {open && (
        <div className="absolute right-0 z-10 mt-1 w-56 rounded-xl border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-800">
          {!isDone && (
            <button type="button" onClick={() => void set('extern_eingereicht')}
              className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700">
              Als extern eingereicht markieren
            </button>
          )}
          {isDone && (
            <button type="button" onClick={() => void set('offen')}
              className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 dark:text-gray-200 dark:hover:bg-gray-700">
              Markierung entfernen (wieder offen)
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function FristBadge({ state }: { state: TaxFristUstva['state'] }): ReactElement {
  if (state === 'eingereicht') return <StateBadge state="eingereicht" />;
  if (state === 'ueberfaellig') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-700 dark:bg-red-900/40 dark:text-red-300">
        <AlertCircle className="h-3.5 w-3.5" /> Überfällig
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-600 dark:bg-gray-700 dark:text-gray-300">
      <Clock className="h-3.5 w-3.5" /> Offen
    </span>
  );
}

function YearPicker({ year, setYear }: { year: number; setYear: (y: number) => void }): ReactElement {
  const cur = new Date().getFullYear();
  return (
    <select
      value={year}
      onChange={(e) => setYear(Number(e.target.value))}
      className="rounded-lg border border-gray-300 px-3 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100"
    >
      {[cur, cur - 1, cur - 2, cur - 3].map((y) => (
        <option key={y} value={y}>{y}</option>
      ))}
    </select>
  );
}

function StateBadge({ state, extern }: { state: 'eingereicht' | 'in_arbeit' | 'offen'; extern?: boolean }): ReactElement {
  if (state === 'eingereicht') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-2.5 py-1 text-xs font-semibold text-green-700 dark:bg-green-900/40 dark:text-green-300">
        <CheckCircle2 className="h-3.5 w-3.5" /> {extern ? 'Extern eingereicht' : 'Eingereicht'}
      </span>
    );
  }
  if (state === 'in_arbeit') {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-semibold text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
        <Clock className="h-3.5 w-3.5" /> In Arbeit
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-xs font-semibold text-gray-600 dark:bg-gray-700 dark:text-gray-300">
      <AlertCircle className="h-3.5 w-3.5" /> Offen
    </span>
  );
}

/** Prognose-Kachel. */
function Stat({ icon: Icon, label, value, hint, tone = 'blue' }: {
  icon: typeof TrendingUp; label: string; value: string; hint?: string; tone?: 'blue' | 'rose' | 'emerald' | 'amber';
}): ReactElement {
  const tones: Record<string, string> = {
    blue: 'text-blue-600 dark:text-blue-400',
    rose: 'text-rose-600 dark:text-rose-400',
    emerald: 'text-emerald-600 dark:text-emerald-400',
    amber: 'text-amber-600 dark:text-amber-400',
  };
  return (
    <Card>
      <div className="flex items-center gap-2 text-sm font-medium text-gray-500 dark:text-gray-400">
        <Icon className={`h-4 w-4 ${tones[tone]}`} /> {label}
      </div>
      <div className="mt-2 text-2xl font-bold text-gray-900 dark:text-gray-100">{value}</div>
      {hint && <div className="mt-1 text-xs text-gray-400">{hint}</div>}
    </Card>
  );
}

export function TaxOverview({ companyId }: { companyId: string }): ReactElement {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState<TaxOverviewData | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    elsterApi.overview(companyId, year)
      .then((d) => { if (alive) { setData(d); setLoading(false); } })
      .catch((e) => { if (alive) { setError(e); setLoading(false); } });
    return () => { alive = false; };
  }, [companyId, year, reload]);

  const todayIso = new Date().toISOString().slice(0, 10);

  if (error) return <Card><ErrorNote error={error} /></Card>;

  const p = data?.prognosen;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-200">Steuerjahr</h2>
        <YearPicker year={year} setYear={setYear} />
      </div>

      {loading && <Card><p className="text-sm text-gray-400">Lade Übersicht…</p></Card>}

      {data && p && (
        <>
          {/* Warnung: moegliche Verspaetungszuschlaege (§152 AO) */}
          {data.verspaetung.ustvaCents > 0 && (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4 dark:border-red-900/50 dark:bg-red-950/30">
              <div className="flex items-start gap-3">
                <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-red-600 dark:text-red-400" />
                <div>
                  <h3 className="text-sm font-semibold text-red-800 dark:text-red-300">
                    Mögliche Verspätungszuschläge: bis zu {euro(data.verspaetung.ustvaCents)}
                  </h3>
                  <p className="mt-1 text-sm text-red-700 dark:text-red-300/90">
                    {data.verspaetung.anzahlUeberfaellig} überfällige Umsatzsteuer-Voranmeldung(en).
                    Bei verspäteter Abgabe kann das Finanzamt einen Verspätungszuschlag (§152 AO) festsetzen:
                    0,25 % der Zahllast je angefangenem Monat, mindestens 25 € pro Monat. Reichen Sie die
                    Voranmeldungen schnellstmöglich ein.
                  </p>
                  <p className="mt-1 text-xs text-red-600/80 dark:text-red-400/70">
                    Schätzung zur Orientierung – die tatsächliche Festsetzung liegt im Ermessen des Finanzamts.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Prognosen (amtliche Formeln) */}
          <div>
            <h3 className="mb-3 text-sm font-semibold text-gray-700 dark:text-gray-200">
              Prognosen {data.year} <span className="font-normal text-gray-400">(Orientierungswerte nach amtlichen Formeln)</span>
            </h3>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Stat icon={TrendingUp} label="Gewinn (EÜR)" value={euro(p.gewinnCents)} tone="emerald"
                hint="Einnahmen minus Ausgaben (laufend)" />
              <Stat icon={Wallet} label="Voraussichtliche ESt" value={euro(p.est.steuerCents)} tone="rose"
                hint={`§32a EStG · ø ${p.est.durchschnittssatzProzent} %`} />
              {data.isKleinunternehmer ? (
                <Stat icon={Building2} label="Umsatzsteuer" value="—" tone="blue" hint="Kleinunternehmer (§19 UStG)" />
              ) : (
                <Stat icon={Building2} label="USt-Zahllast (Jahr)" value={euro(p.ustZahllastCents)} tone="blue"
                  hint="Vereinnahmte USt − Vorsteuer" />
              )}
              {p.gewerbesteuer ? (
                <Stat icon={Building2} label="Gewerbesteuer" value={euro(p.gewerbesteuer.steuerCents)} tone="amber"
                  hint={`3,5 % Messzahl · Hebesatz ${p.gewerbesteuer.hebesatzProzent} %`} />
              ) : (
                <Stat icon={PiggyBank} label="Rücklage-Empfehlung" value={euro(p.ruecklage.empfohlenCents)} tone="amber"
                  hint={`≈ ${p.ruecklage.prozentVomGewinn} % des Gewinns zurücklegen`} />
              )}
            </div>
            {p.gewerbesteuer && (
              <div className="mt-4">
                <Stat icon={PiggyBank} label="Rücklage-Empfehlung (Steuern gesamt)" value={euro(p.ruecklage.empfohlenCents)} tone="amber"
                  hint={`ESt + GewSt${data.isKleinunternehmer ? '' : ' + USt'} ≈ ${p.ruecklage.prozentVomGewinn} % des Gewinns`} />
              </div>
            )}
          </div>

          {/* Pflichten & Status */}
          <div>
            <h3 className="mb-3 text-sm font-semibold text-gray-700 dark:text-gray-200">
              Erklärungen {data.year}
            </h3>
            {data.ustvaRhythmusFehlt && (
              <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-300">
                <AlertCircle className="mr-1.5 inline h-4 w-4" />
                Bitte legen Sie in den{' '}
                <a href="/app/einstellungen" className="font-medium underline">Einstellungen → Firma</a>{' '}
                fest, ob die Umsatzsteuer-Voranmeldung monatlich, vierteljährlich oder jährlich abzugeben ist.
              </div>
            )}
            <Card className="divide-y divide-gray-100 dark:divide-gray-700">
              {/* UStVA hat eine eigene Zeitraeume-Liste unten -> hier ausblenden.
                  Sortierung: Dringendstes oben (ueberfaellig -> offen -> erledigt). */}
              {data.pflichten
                .filter((pf) => pf.type !== 'ustva')
                .map((pf) => ({ pf, frist: data.fristen.jahres.find((j) => j.type === pf.type) }))
                .sort((a, b) => {
                  const ra = urgencyRank(a.pf.state, a.frist?.dueDate, todayIso);
                  const rb = urgencyRank(b.pf.state, b.frist?.dueDate, todayIso);
                  if (ra !== rb) return ra - rb;
                  return (a.frist?.dueDate ?? '').localeCompare(b.frist?.dueDate ?? '');
                })
                .map(({ pf, frist }) => {
                // Jahresfrist (31.07. Folgejahr); ueberfaellig wenn offen & ueberschritten.
                const ueberfaellig = pf.state !== 'eingereicht' && !!frist && frist.dueDate < todayIso;
                return (
                  <div key={pf.type} className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{pf.label}</div>
                      <div className="flex items-center gap-2 text-xs text-gray-400">
                        <span>{pf.rhythmus}</span>
                        {pf.type !== 'ustva' && frist && (
                          <span className={`inline-flex items-center gap-1 ${ueberfaellig ? 'font-semibold text-red-500' : ''}`}>
                            <CalendarClock className="h-3 w-3" /> fällig am {fmtDate(frist.dueDate)}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {ueberfaellig ? (
                        <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-1 text-xs font-semibold text-red-700 dark:bg-red-900/40 dark:text-red-300">
                          <AlertCircle className="h-3.5 w-3.5" /> Überfällig
                        </span>
                      ) : (
                        <StateBadge state={pf.state} extern={pf.extern} />
                      )}
                      {pf.type !== 'ustva' && (
                        <StatusMenu companyId={companyId} type={pf.type} period={String(data.year)}
                          isDone={pf.state === 'eingereicht'} onDone={() => setReload((r) => r + 1)} />
                      )}
                    </div>
                  </div>
                );
              })}
            </Card>
            <p className="mt-2 text-xs text-gray-400">
              Pflichten automatisch aus deinem Firmenprofil abgeleitet
              {data.isKleinunternehmer ? ' (Kleinunternehmer §19 UStG – keine Umsatzsteuer)' : ''}.
            </p>
          </div>

          {/* UStVA-Zeitraeume mit Fristen */}
          {data.fristen.ustva.length > 0 && (
            <div>
              <h3 className="mb-3 text-sm font-semibold text-gray-700 dark:text-gray-200">
                UStVA-Zeiträume {data.year}
              </h3>
              <Card className="divide-y divide-gray-100 dark:divide-gray-700">
                {[...data.fristen.ustva]
                  .sort((a, b) => {
                    const ra = urgencyRank(a.state, a.dueDate, todayIso);
                    const rb = urgencyRank(b.state, b.dueDate, todayIso);
                    if (ra !== rb) return ra - rb;
                    return a.dueDate.localeCompare(b.dueDate);
                  })
                  .map((f) => (
                  <div key={f.period} className="flex items-center justify-between gap-3 py-2.5 first:pt-0 last:pb-0">
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{f.label}</div>
                      <div className="flex items-center gap-1 text-xs text-gray-400">
                        <CalendarClock className="h-3 w-3" /> fällig am {fmtDate(f.dueDate)}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <FristBadge state={f.state} />
                      <StatusMenu companyId={companyId} type="ustva" period={f.period}
                        isDone={f.state === 'eingereicht'} onDone={() => setReload((r) => r + 1)} />
                    </div>
                  </div>
                ))}
              </Card>
            </div>
          )}

          <Bescheide companyId={companyId} />

          <p className="rounded-lg bg-gray-50 px-3 py-2 text-xs text-gray-500 dark:bg-gray-800 dark:text-gray-400">
            Hinweis: Prognosen sind Orientierungswerte nach den amtlichen Grundformeln (§32a EStG, Gewerbesteuer-Messzahl 3,5 %).
            Sie ersetzen keine steuerliche Beratung; maßgeblich bleiben Finanzamt/Steuerberater.
          </p>
        </>
      )}
    </div>
  );
}
